import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Menu, Sun, Moon, Globe } from "lucide-react";
import { useTheme } from "next-themes";
import { useLanguage } from "../context/LanguageContext";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";

const LOGO_URL = "/assets/logo.png";

export default function Navbar() {
    const { t, lang, setLang } = useLanguage();
    const { theme, setTheme } = useTheme();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);


    const navItems = [
        { label: t.nav.services, href: "servicios" },
        { label: t.nav.quote, href: "presupuesto" },
        { label: t.nav.languages, href: "idiomas" },
        { label: t.nav.about, href: "sobre-nosotros" },
        { label: t.nav.payment, href: "formas-de-pago" },
        { label: t.nav.contact, href: "contacto" },
    ];

    const scrollTo = (id) => {
        if (pathname !== "/" && pathname !== "/privacy-policy" && pathname !== "/politica-de-privacidad" && pathname !== "/cookie-policy" && pathname !== "/politica-de-cookies") {
             // Basic check, actually we want to go home if not on /
        }

        if (pathname === "/") {
            document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
        } else {
            // If not on home, go home first with the hash
            navigate(`/#${id}`);
        }
        setIsOpen(false);
    };

    // Effect to handle scroll after navigation if hash is present
    useEffect(() => {
        if (pathname === "/" && window.location.hash) {
            const id = window.location.hash.substring(1);
            setTimeout(() => {
                document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
            }, 100);
        }
    }, [pathname]);


    return (
        <nav
            className="sticky top-0 z-50 glass-nav bg-white/80 dark:bg-slate-950/80 border-b border-slate-200/60 dark:border-slate-800/60"
            data-testid="navbar"
        >
            <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
                <Link to="/" className="flex-shrink-0" data-testid="navbar-logo">
                    <img
                        src={LOGO_URL}
                        alt="Global Translations"
                        className="h-11 w-auto rounded"
                    />
                </Link>

                <div className="hidden lg:flex items-center gap-8">
                    {navItems.map((item) => (
                        <button
                            key={item.href}
                            onClick={() => scrollTo(item.href)}
                            className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-[#0A1628] dark:hover:text-white transition-colors duration-200"
                            data-testid={`nav-${item.href}`}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setLang(lang === "es" ? "en" : "es")}
                        className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors duration-200"
                        data-testid="language-selector"
                    >
                        <Globe className="w-4 h-4" />
                        <span>{lang === "es" ? "ES" : "EN"}</span>
                    </button>

                    <button
                        onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                        className="p-2 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors duration-200"
                        data-testid="theme-toggle"
                    >
                        {theme === "dark" ? (
                            <Sun className="w-4 h-4" />
                        ) : (
                            <Moon className="w-4 h-4" />
                        )}
                    </button>

                    <Sheet open={isOpen} onOpenChange={setIsOpen}>
                        <SheetTrigger asChild className="lg:hidden">
                            <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                                <Menu className="w-5 h-5" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="right">
                            <SheetTitle className="sr-only">Menu</SheetTitle>
                            <div className="flex flex-col gap-6 mt-8">
                                {navItems.map((item) => (
                                    <button
                                        key={item.href}
                                        onClick={() => scrollTo(item.href)}
                                        className="text-lg font-medium text-left hover:text-blue-600 transition-colors"
                                        data-testid={`mobile-nav-${item.href}`}
                                    >
                                        {item.label}
                                    </button>
                                ))}
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </nav>
    );
}
